import { z } from 'zod'
import { GESTES_N2, type Evaluation } from './consignes'
import { TYPES_REFERENCE, type TypeReference } from './domaine'
import { normaliserLibelle } from './libelle'

// Consignes de fournisseur : la mémoire du rattachement des pièces d'un fournisseur qui sert
// plusieurs endroits (énergie, eau, télécom). Chacune, rattachée à un tiers, mène à une
// entité : par des critères structurés (une référence lue sur la pièce : PRM, contrat,
// adresse du point de livraison…), comparés sans modèle, ou, à défaut, par un texte libre
// télégraphique que Claude ne lit que pour une pièce de ce fournisseur qu'aucun critère n'a
// couverte. Écrites ou validées par un humain, elles ne font que du N0 (rattacher).
// Fonctions pures.

export const LIBELLES_REFERENCE: Record<TypeReference, string> = {
  prm: 'PRM',
  pce: 'PCE',
  contrat: 'Contrat',
  client: 'Client',
  ligne: 'Ligne',
  adresse: 'Adresse',
}

/** Une référence lue sur la pièce, telle qu'écrite (comparée normalisée). */
export const referencePiece = z.object({
  type: z.enum(TYPES_REFERENCE),
  valeur: z.string().trim().min(1).max(200),
})
export type ReferencePiece = z.infer<typeof referencePiece>

/** Abréviations postales courantes, développées pour comparer une adresse. */
const ABREVIATIONS: Record<string, string> = {
  R: 'RUE',
  AV: 'AVENUE',
  AVE: 'AVENUE',
  BD: 'BOULEVARD',
  BLD: 'BOULEVARD',
  PL: 'PLACE',
  CHE: 'CHEMIN',
  CHEM: 'CHEMIN',
  IMP: 'IMPASSE',
  ALL: 'ALLEE',
  RTE: 'ROUTE',
  FBG: 'FAUBOURG',
  ST: 'SAINT',
  STE: 'SAINTE',
}

/** Adresse comparable : sans accents ni ponctuation, en majuscules, abréviations développées. */
function adresseNormalisee(t: string): string {
  return normaliserLibelle(t)
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((m) => ABREVIATIONS[m] ?? m)
    .join(' ')
}

/**
 * La forme comparable d'une référence : numéros sans séparateurs, en majuscules ; ligne en
 * chiffres, l'indicatif +33 ramené au 0 ; adresse normalisée mot à mot.
 */
export function normaliserReference(type: TypeReference, valeur: string): string {
  if (type === 'adresse') return adresseNormalisee(valeur)
  if (type === 'ligne') {
    const chiffres = valeur.replace(/\D/g, '')
    return chiffres.length === 11 && chiffres.startsWith('33') ? `0${chiffres.slice(2)}` : chiffres
  }
  return normaliserLibelle(valeur).replace(/[^A-Z0-9]/g, '')
}

/**
 * Le critère se lit-il sur la pièce ? Même type ; un numéro identique une fois normalisé ;
 * une adresse qui contient le fragment, mot pour mot (« 14 RUE DES LILAS » se lit dans
 * « 14 R DES LILAS 86100 », pas dans « 114 RUE DES LILAS »).
 */
export function critereCorrespond(critere: ReferencePiece, reference: ReferencePiece): boolean {
  if (critere.type !== reference.type) return false
  const c = normaliserReference(critere.type, critere.valeur)
  const r = normaliserReference(reference.type, reference.valeur)
  if (!c) return false
  if (critere.type === 'adresse') return ` ${r} `.includes(` ${c} `)
  return c === r
}

/** « Contrat 900001 », « Adresse 14 rue des Lilas ». */
export function decrireCritere(c: ReferencePiece): string {
  return `${LIBELLES_REFERENCE[c.type]} ${c.valeur}`
}

/** Saisie lisible : « Contrat : 900001 », comme elle s'écrit dans l'appli. */
export function formaterCritere(c: ReferencePiece): string {
  return `${LIBELLES_REFERENCE[c.type]} : ${c.valeur}`
}

/** Libellés reconnus à la saisie (sans casse ni accents), « n° » et « numéro » ignorés. */
const SAISIE_TYPE: Record<string, TypeReference> = {
  PRM: 'prm',
  PDL: 'prm',
  PCE: 'pce',
  CONTRAT: 'contrat',
  CLIENT: 'client',
  LIGNE: 'ligne',
  ADRESSE: 'adresse',
}

/** Une ligne « type : valeur » ; null si elle ne se lit pas. */
export function analyserCritere(ligne: string): ReferencePiece | null {
  const m = /^([^:]+):(.*)$/.exec(ligne)
  if (!m) return null
  const libelle = normaliserLibelle(m[1]!)
    .replace(/\b(N°|NO|NUMERO|N)\b\.?/g, ' ')
    .replace(/[^A-Z]/g, '')
  const type = SAISIE_TYPE[libelle]
  const valeur = m[2]!.trim()
  return type && valeur ? { type, valeur } : null
}

/**
 * Les références d'une pièce proposées pour retenir une consigne : numéros d'abord (les plus
 * sûrs), adresse ensuite, sans doublon.
 */
export function referencesProposees(refs: readonly ReferencePiece[]): ReferencePiece[] {
  const vues = new Set<string>()
  return [...refs]
    .sort((a, b) => TYPES_REFERENCE.indexOf(a.type) - TYPES_REFERENCE.indexOf(b.type))
    .filter((r) => {
      const cle = `${r.type}:${normaliserReference(r.type, r.valeur)}`
      if (vues.has(cle) || cle.endsWith(':')) return false
      vues.add(cle)
      return true
    })
}

/** Une consigne de fournisseur active, telle que le rattachement la lit. */
export type ConsigneFournisseurRef = {
  id: string
  tiersId: string
  entiteId: string
  criteres: readonly ReferencePiece[]
  texte: string | null
}

export type IssueConsignes =
  /** Le fournisseur n'a pas de consigne : son entité par défaut vaut. */
  | { issue: 'aucune' }
  | { issue: 'tranche'; consigne: ConsigneFournisseurRef; par: 'critere' | 'texte' }
  /** Des critères mènent à des entités différentes : un humain tranche. */
  | { issue: 'contradiction' }
  /** Aucun critère ne tranche ; ces consignes ont un texte à soumettre à Claude. */
  | { issue: 'a_juger'; consigneIds: string[] }
  /** Nouvel endroit : aucune consigne ne le couvre. Jamais l'entité par défaut. */
  | { issue: 'non_couverte' }

/**
 * Les consignes d'un fournisseur (les siennes seulement) appliquées aux références lues sur sa
 * pièce. Les critères d'abord, sans modèle ; le texte libre n'est jugé que si aucun critère
 * n'a tranché : `parTexte` absent, il reste à juger ; null, Claude n'en a retenu aucune (ou
 * n'a pas pu juger) ; sinon l'identifiant de la consigne retenue.
 */
export function appliquerConsignesFournisseur(
  references: readonly ReferencePiece[],
  consignes: readonly ConsigneFournisseurRef[],
  parTexte?: string | null,
): IssueConsignes {
  if (consignes.length === 0) return { issue: 'aucune' }
  const touchees = consignes.filter((c) =>
    c.criteres.some((cr) => references.some((r) => critereCorrespond(cr, r))),
  )
  if (touchees.length > 0) {
    const entites = new Set(touchees.map((c) => c.entiteId))
    return entites.size === 1
      ? { issue: 'tranche', consigne: touchees[0]!, par: 'critere' }
      : { issue: 'contradiction' }
  }
  const aTexte = consignes.filter((c) => c.texte !== null)
  if (parTexte === undefined && aTexte.length > 0)
    return { issue: 'a_juger', consigneIds: aTexte.map((c) => c.id) }
  const retenue = parTexte ? aTexte.find((c) => c.id === parTexte) : undefined
  return retenue ? { issue: 'tranche', consigne: retenue, par: 'texte' } : { issue: 'non_couverte' }
}

// Saisie

/** Un critère utile : un numéro d'au moins trois caractères, une adresse d'au moins deux mots. */
export const critereSaisi = referencePiece.superRefine((c, ctx) => {
  const n = normaliserReference(c.type, c.valeur)
  if (c.type === 'adresse') {
    if (n.split(' ').length < 2 || n.length < 8)
      ctx.addIssue({
        code: 'custom',
        message: 'Adresse trop vague : numéro et voie au moins (« 14 rue des Lilas »).',
      })
  } else if (n.length < 3)
    ctx.addIssue({ code: 'custom', message: 'Référence trop courte : trois caractères au moins.' })
})

export const LIMITES_CONSIGNE_FOURNISSEUR = { criteres: 10, texte: 300 } as const

const criteres = z.array(critereSaisi).max(LIMITES_CONSIGNE_FOURNISSEUR.criteres)
const texteLibre = z.string().trim().min(1).max(LIMITES_CONSIGNE_FOURNISSEUR.texte).nullable()

/** « Retenir pour ce fournisseur » au rattachement d'une pièce : l'entité est celle choisie. */
export const consigneDepuisPiece = z.object({
  criteres: criteres.default([]),
  texte: texteLibre.default(null),
})

export const consigneFournisseurCreation = z
  .object({ entiteId: z.uuid(), criteres: criteres.default([]), texte: texteLibre.default(null) })
  .refine((c) => c.criteres.length > 0 || c.texte !== null, {
    error: 'Un critère (référence lue sur la pièce) ou un texte, au moins.',
  })
export const consigneFournisseurModification = z
  .object({ entiteId: z.uuid(), criteres, texte: texteLibre })
  .partial()

export const actionConsigneFournisseur = z.object({ action: z.enum(['activer', 'desactiver']) })

export type ConsigneFournisseurCreation = z.infer<typeof consigneFournisseurCreation>
export type ConsigneFournisseurModification = z.infer<typeof consigneFournisseurModification>

/** Une consigne écrite doit garder un critère ou un texte. */
export function erreursFormeConsigneFournisseur(c: {
  criteres: readonly ReferencePiece[]
  texte: string | null
}): string[] {
  return c.criteres.length > 0 || c.texte !== null
    ? []
    : ['Un critère (référence lue sur la pièce) ou un texte, au moins.']
}

/**
 * Grille d'une consigne de fournisseur, face aux autres consignes du même fournisseur (non
 * désactivées) : un critère déjà retenu vers une autre entité est une erreur (les deux se
 * contrediraient) ; vers la même, un avertissement. Texte seul : Claude jugera, à chaque
 * pièce non couverte. Payer, déclarer, signer : jamais.
 */
export function evaluerConsigneFournisseur(
  c: { id: string; entiteId: string; criteres: readonly ReferencePiece[]; texte: string | null },
  autres: readonly ConsigneFournisseurRef[],
): Evaluation {
  const erreurs: string[] = []
  const avertissements: string[] = []
  for (const cr of c.criteres) {
    const memes = autres.filter(
      (a) =>
        a.id !== c.id &&
        a.criteres.some((x) => critereCorrespond(x, cr) || critereCorrespond(cr, x)),
    )
    if (memes.some((a) => a.entiteId !== c.entiteId))
      erreurs.push(
        `${decrireCritere(cr)} est déjà retenu par une autre consigne de ce fournisseur, vers une autre entité.`,
      )
    else if (memes.length > 0)
      avertissements.push(`${decrireCritere(cr)} est déjà retenu pour la même entité.`)
  }
  if (c.criteres.length === 0)
    avertissements.push(
      'Sans critère sûr : Claude jugera le texte pour chaque pièce de ce fournisseur qu’aucun critère ne couvre.',
    )
  if (c.texte && GESTES_N2.test(c.texte))
    avertissements.push(
      'Payer, déclarer et signer restent humains : la consigne ne fera que rattacher.',
    )
  return { erreurs, avertissements }
}
